import { useEffect, useState } from "react";
import { Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import ImageUpload from "@/components/admin/ImageUpload";

const DEFAULTS = {
  primary_color: "#FA4A0C",
  secondary_color: "#22C55E",
  background_color: "#F8FAFC",
  button_color: "#FA4A0C",
  text_color: "#0F172A",
  card_style: "rounded",
};

const CARD_STYLES = [
  { value: "rounded", label: "Arredondado" },
  { value: "compact", label: "Compacto" },
  { value: "large-image", label: "Imagem grande" },
  { value: "small-image", label: "Imagem pequena" },
  { value: "simple", label: "Simples" },
];

const ColorField = ({ label, value, onChange, name }) => (
  <div>
    <label className="text-sm font-medium text-foreground mb-1 block">{label}</label>
    <div className="flex items-center gap-2">
      <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} data-testid={`color-${name}`} className="w-10 h-10 rounded-lg border border-border cursor-pointer" />
      <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-border bg-background outline-none focus:border-primary text-sm font-mono" />
    </div>
  </div>
);

const LayoutCustomizer = () => {
  const { settings, reload } = useSite();
  const [form, setForm] = useState(null);

  useEffect(() => { if (settings) setForm({ ...settings }); }, [settings]);
  if (!form) return null;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    try {
      await api.put("/admin/settings", form);
      toast.success("Layout salvo");
      reload();
    } catch (e) {
      toast.error("Erro ao salvar");
    }
  };

  const restore = () => setForm((p) => ({ ...p, ...DEFAULTS }));

  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold font-heading text-slate-900">Personalizar Layout</h1>
          <p className="text-sm text-muted-foreground">Modifique a aparência do site sem editar código</p>
        </div>
        <div className="flex gap-2">
          <button onClick={restore} data-testid="restore-btn" className="flex items-center gap-2 border border-border rounded-full px-4 py-2.5 text-sm font-semibold hover:bg-slate-100 transition-colors">
            <RotateCcw className="w-4 h-4" /> Restaurar padrão
          </button>
          <button onClick={save} data-testid="save-layout-btn" className="flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-4 py-2.5 text-sm font-semibold active:scale-95 transition-transform">
            <Save className="w-4 h-4" /> Salvar alterações
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl border border-border p-5 space-y-4">
          <h2 className="font-bold font-heading text-slate-900">Identidade</h2>
          <div>
            <label className="text-sm font-medium mb-1 block">Nome do site</label>
            <input value={form.site_name || ""} onChange={(e) => set("site_name", e.target.value)} data-testid="setting-site-name" className="w-full px-3 py-2 rounded-lg border border-border bg-background outline-none focus:border-primary text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Slogan</label>
            <input value={form.slogan || ""} onChange={(e) => set("slogan", e.target.value)} className="w-full px-3 py-2 rounded-lg border border-border bg-background outline-none focus:border-primary text-sm" />
          </div>
          <ImageUpload value={form.logo_url} onChange={(v) => set("logo_url", v)} label="Logotipo" />
          <ImageUpload value={form.favicon_url} onChange={(v) => set("favicon_url", v)} label="Favicon" />
        </div>

        <div className="bg-white rounded-2xl border border-border p-5 space-y-4">
          <h2 className="font-bold font-heading text-slate-900">Cores</h2>
          <ColorField label="Cor principal" name="primary" value={form.primary_color} onChange={(v) => set("primary_color", v)} />
          <ColorField label="Cor secundária (botões WhatsApp)" name="secondary" value={form.secondary_color} onChange={(v) => set("secondary_color", v)} />
          <ColorField label="Cor de fundo" name="background" value={form.background_color} onChange={(v) => set("background_color", v)} />
          <ColorField label="Cor dos textos" name="text" value={form.text_color} onChange={(v) => set("text_color", v)} />
        </div>

        <div className="bg-white rounded-2xl border border-border p-5 space-y-4">
          <h2 className="font-bold font-heading text-slate-900">Estilo dos cards</h2>
          <div className="grid grid-cols-2 gap-2">
            {CARD_STYLES.map((c) => (
              <button key={c.value} onClick={() => set("card_style", c.value)} data-testid={`card-style-${c.value}`} className={`px-3 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${form.card_style === c.value ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-border p-5">
          <h2 className="font-bold font-heading text-slate-900 mb-3">Pré-visualização</h2>
          <div className="rounded-2xl p-4" style={{ background: form.background_color }}>
            <div className="rounded-2xl bg-white border p-4 shadow-sm">
              <div className="h-20 rounded-xl mb-3" style={{ background: form.primary_color }} />
              <div className="font-bold" style={{ color: form.text_color }}>{form.site_name}</div>
              <p className="text-xs mt-1" style={{ color: form.text_color, opacity: 0.6 }}>{form.slogan}</p>
              <button className="mt-3 text-white text-xs font-semibold rounded-full px-4 py-2" style={{ background: form.secondary_color }}>Pedir pelo WhatsApp</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LayoutCustomizer;
